// src/services/assetsAPI.ts
// API service for qmLEO Assets Module
// Created: 2025-01-31
// Updated: 2025-02-01 - Added verification methods, report methods

import { authService } from './authService';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from './configService';
import {
  TrackedAsset,
  AssetCategory,
  AssetVerification,
  CreateAssetRequest,
  UpdateAssetRequest,
  DisposeAssetRequest,
  CreateAssetCategoryRequest,
  UpdateAssetCategoryRequest,
  CreateVerificationRequest,
  AssetAPIResponse,
  ListAssetsResponse,
  ListAssetCategoriesResponse,
  ListVerificationsResponse,
  AssetListFilters,
  VerificationListFilters
} from '../types/asset';
import {
  AssetReportType,
  AssetReportFilters,
  VerificationStatusReport,
  OverdueReport,
  InventoryReport,
  DisposalReport,
  ValueSummaryReport
} from '../types/assetReports';

const assetLogger = logger.createServiceLogger('AssetsAPI');

export class AssetsAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiBaseUrl();
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<AssetAPIResponse<T>> {
    const endTimer = logger.startTimer('AssetsAPI', `${options.method || 'GET'} ${endpoint}`);
    
    logger.logAPIRequest('AssetsAPI', options.method || 'GET', `${this.baseUrl}${endpoint}`, options.body);

    const hasBody = options.body !== undefined && options.body !== null;
    const headers = new Headers(options.headers || {});
    if (hasBody && !headers.has('Content-Type') && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    try {
      const response = await authService.makeAuthenticatedRequest(
        `${this.baseUrl}${endpoint}`,
        { ...options, headers }
      );

      logger.logAPIResponse('AssetsAPI', options.method || 'GET', endpoint, response.status);
      endTimer();

      const contentType = response.headers.get('content-type') || '';
      
      // Handle CSV responses
      if (contentType.includes('text/csv')) {
        const csvText = await response.text();
        return { success: true, data: csvText as unknown as T };
      }

      const data = contentType.includes('application/json')
        ? await response.json()
        : (undefined as unknown as AssetAPIResponse<T>);

      if (!response.ok) {
        assetLogger.error(`Request failed with status ${response.status}`, { 
          endpoint, 
          status: response.status, 
          data 
        });
        return {
          success: false,
          error: (data && (data as any).error) || {
            code: 'HTTP_ERROR',
            message: `HTTP ${response.status}`,
          },
        };
      }

      return (data ?? { success: true }) as AssetAPIResponse<T>;
    } catch (error: any) {
      endTimer();
      assetLogger.error('Request failed', { endpoint, method: options.method }, error);

      if (error.code === 'NO_SESSION') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Please log in again' },
        };
      }

      if (error.code === 'TOKEN_EXPIRED') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Session expired, please refresh' },
        };
      }

      if (error.code === 'NETWORK_ERROR') {
        return {
          success: false,
          error: { code: 'NETWORK_ERROR', message: error.message },
        };
      }

      return {
        success: false,
        error: { code: 'REQUEST_ERROR', message: error.message || 'Request failed' },
      };
    }
  }

  // ===== TRACKED ASSETS =====

  async getAssets(filters?: AssetListFilters): Promise<AssetAPIResponse<ListAssetsResponse>> {
    assetLogger.debug('Fetching assets', { filters });
    
    const params = new URLSearchParams();
    if (filters) {
     if (filters.status) params.append('status', filters.status);
      if (filters.categoryId) params.append('categoryId', filters.categoryId);
      if (filters.verificationStatus && filters.verificationStatus !== 'all') {
        params.append('verificationStatus', filters.verificationStatus);
      }
      if (filters.search) params.append('search', filters.search);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.lastEvaluatedKey) params.append('lastEvaluatedKey', filters.lastEvaluatedKey);
    }

    const queryString = params.toString();
    const endpoint = queryString ? `/assets?${queryString}` : '/assets';
    
    return this.makeRequest<ListAssetsResponse>(endpoint);
  }

  async getAsset(assetId: string): Promise<AssetAPIResponse<{ asset: TrackedAsset }>> {
    assetLogger.debug('Fetching asset', { assetId });
    return this.makeRequest<{ asset: TrackedAsset }>(`/assets/${assetId}`);
  }

  async createAsset(assetData: CreateAssetRequest): Promise<AssetAPIResponse<{ asset: TrackedAsset }>> {
    assetLogger.info('Creating new asset', { 
      assetTag: assetData.assetTag,
      name: assetData.name,
      categoryId: assetData.categoryId
    });
    return this.makeRequest<{ asset: TrackedAsset }>('/assets', {
      method: 'POST',
      body: JSON.stringify(assetData),
    });
  }

  async updateAsset(assetId: string, assetData: UpdateAssetRequest): Promise<AssetAPIResponse<{ asset: TrackedAsset }>> {
    assetLogger.info('Updating asset', { 
      assetId,
      fieldsUpdated: Object.keys(assetData)
    });
    return this.makeRequest<{ asset: TrackedAsset }>(`/assets/${assetId}`, {
      method: 'PUT',
      body: JSON.stringify(assetData),
    });
  }

  async disposeAsset(assetId: string, disposalData: DisposeAssetRequest): Promise<AssetAPIResponse<{ asset: TrackedAsset }>> {
    assetLogger.warn('Disposing asset', { 
      assetId,
      method: disposalData.disposalMethod
    });
    return this.makeRequest<{ asset: TrackedAsset }>(`/assets/${assetId}/dispose`, {
      method: 'POST',
      body: JSON.stringify(disposalData),
    });
  }

  async verifyAsset(assetId: string, verificationData: Omit<CreateVerificationRequest, 'assetId'>): Promise<AssetAPIResponse<{ verification: AssetVerification; asset: TrackedAsset }>> {
    assetLogger.info('Verifying asset', { 
      assetId,
      location: verificationData.locationVerified,
      condition: verificationData.condition
    });
    return this.makeRequest<{ verification: AssetVerification; asset: TrackedAsset }>(`/assets/${assetId}/verify`, {
      method: 'POST',
      body: JSON.stringify(verificationData),
    });
  }

  // ===== ASSET VERIFICATIONS =====

  async getVerifications(filters?: VerificationListFilters): Promise<AssetAPIResponse<ListVerificationsResponse>> {
    assetLogger.debug('Fetching verifications', { filters });
    
    const params = new URLSearchParams();
    if (filters) {
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.limit) params.append('limit', filters.limit.toString());
    }

    const queryString = params.toString();
    const endpoint = queryString ? `/asset-verifications?${queryString}` : '/asset-verifications';
    
    return this.makeRequest<ListVerificationsResponse>(endpoint);
  }

  async getAssetVerificationHistory(assetId: string, limit?: number): Promise<AssetAPIResponse<ListVerificationsResponse & { assetId: string }>> {
    assetLogger.debug('Fetching verification history for asset', { assetId, limit });
    
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());

    const queryString = params.toString();
    const endpoint = queryString 
      ? `/asset-verifications/asset/${assetId}?${queryString}` 
      : `/asset-verifications/asset/${assetId}`;
    
    return this.makeRequest<ListVerificationsResponse & { assetId: string }>(endpoint);
  }

  // ===== ASSET CATEGORIES =====

  async getCategories(): Promise<AssetAPIResponse<ListAssetCategoriesResponse>> {
    assetLogger.debug('Fetching asset categories');
    return this.makeRequest<ListAssetCategoriesResponse>('/asset-categories');
  }

  async createCategory(categoryData: CreateAssetCategoryRequest): Promise<AssetAPIResponse<{ category: AssetCategory }>> {
    assetLogger.info('Creating asset category', { name: categoryData.name });
    return this.makeRequest<{ category: AssetCategory }>('/asset-categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  async updateCategory(categoryId: string, categoryData: UpdateAssetCategoryRequest): Promise<AssetAPIResponse<{ category: AssetCategory }>> {
    assetLogger.info('Updating asset category', { 
      categoryId,
      fieldsUpdated: Object.keys(categoryData)
    });
    return this.makeRequest<{ category: AssetCategory }>(`/asset-categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    });
  }

  async initializeDefaultCategories(): Promise<AssetAPIResponse<{ created: number; categories: AssetCategory[] }>> {
    assetLogger.info('Initializing default asset categories');
    return this.makeRequest<{ created: number; categories: AssetCategory[] }>('/asset-categories/initialize', {
      method: 'POST',
    });
  }

  // ===== ASSET REPORTS =====

  private buildReportQueryString(filters?: AssetReportFilters): string {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.categoryId) params.append('categoryId', filters.categoryId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.includeDisposed) params.append('includeDisposed', 'true');
      if (filters.format) params.append('format', filters.format);
    }
    return params.toString();
  }

  async getVerificationStatusReport(filters?: AssetReportFilters): Promise<AssetAPIResponse<VerificationStatusReport>> {
    assetLogger.debug('Fetching verification status report', { filters });
    const queryString = this.buildReportQueryString(filters);
    const endpoint = queryString 
      ? `/asset-reports/verification-status?${queryString}` 
      : '/asset-reports/verification-status';
    return this.makeRequest<VerificationStatusReport>(endpoint);
  }

  async getOverdueReport(filters?: AssetReportFilters): Promise<AssetAPIResponse<OverdueReport>> {
    assetLogger.debug('Fetching overdue assets report', { filters });
    const queryString = this.buildReportQueryString(filters);
    const endpoint = queryString 
      ? `/asset-reports/overdue?${queryString}` 
      : '/asset-reports/overdue';
    return this.makeRequest<OverdueReport>(endpoint);
  }

  async getInventoryReport(filters?: AssetReportFilters): Promise<AssetAPIResponse<InventoryReport>> {
    assetLogger.debug('Fetching asset inventory report', { filters });
    const queryString = this.buildReportQueryString(filters);
    const endpoint = queryString 
      ? `/asset-reports/inventory?${queryString}` 
      : '/asset-reports/inventory';
    return this.makeRequest<InventoryReport>(endpoint);
  }

  async getDisposalReport(filters?: AssetReportFilters): Promise<AssetAPIResponse<DisposalReport>> {
    assetLogger.debug('Fetching disposal report', { filters });
    const queryString = this.buildReportQueryString(filters);
    const endpoint = queryString 
      ? `/asset-reports/disposal?${queryString}` 
      : '/asset-reports/disposal';
    return this.makeRequest<DisposalReport>(endpoint);
  }

  async getValueSummaryReport(filters?: AssetReportFilters): Promise<AssetAPIResponse<ValueSummaryReport>> {
    assetLogger.debug('Fetching value summary report', { filters });
    const queryString = this.buildReportQueryString(filters);
    const endpoint = queryString 
      ? `/asset-reports/value-summary?${queryString}` 
      : '/asset-reports/value-summary';
    return this.makeRequest<ValueSummaryReport>(endpoint);
  }

  async downloadReportCSV(reportType: AssetReportType, filters?: Omit<AssetReportFilters, 'format'>): Promise<AssetAPIResponse<string>> {
    assetLogger.info('Downloading report as CSV', { reportType, filters });
    const queryString = this.buildReportQueryString({ ...filters, format: 'csv' });
    const endpoint = `/asset-reports/${reportType}?${queryString}`;
    return this.makeRequest<string>(endpoint);
  }
}

// Export singleton instance
export const assetsAPI = new AssetsAPI();
